
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CommunityMember } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AutosuggestInput } from "./AutosuggestInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type StaffVisitDialogProps = {
  staff: CommunityMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisitSaved?: () => void;
};

export function StaffVisitDialog({ staff, open, onOpenChange, onVisitSaved }: StaffVisitDialogProps) {
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [temperature, setTemperature] = useState<string>("36.2");
  const [temperatureError, setTemperatureError] = useState<string>("");
  const [requiresFollowup, setRequiresFollowup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use the profileId directly from the user object
  const { data: nurseProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['nurseProfile', user?.profileId],
    queryFn: async () => {
      console.log('Using nurse profile ID directly:', user?.profileId);
      
      if (!user?.profileId) {
        console.error('No profile ID found in user object');
        throw new Error('Nurse profile not found');
      }
      
      // Return the profile ID directly
      return { id: user.profileId };
    },
    enabled: !!user?.profileId,
    retry: 2
  });

  const validateTemperature = (value: string) => {
    const temp = parseFloat(value);
    if (isNaN(temp)) {
      setTemperatureError("Please enter a valid number");
      return false;
    }
    if (temp < 32.5 || temp > 42) {
      setTemperatureError("Temperature must be between 32.5°C and 42°C");
      return false;
    }
    setTemperatureError("");
    return true;
  };

  const handleTemperatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemperature(value);
    validateTemperature(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staff || isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!nurseProfile?.id) {
        throw new Error("Nurse profile not found. Please make sure you're logged in properly.");
      }

      const { data: activeYear } = await supabase
        .from("academic_years")
        .select("id")
        .eq("is_active", true)
        .maybeSingle();

      if (!activeYear?.id) {
        throw new Error("No active academic year found");
      }

      const { data: activeTerm } = await supabase
        .from("terms")
        .select("id")
        .eq("is_active", true)
        .eq("academic_year_id", activeYear.id)
        .maybeSingle();

      if (!activeTerm?.id) {
        throw new Error("No active term found");
      }

      console.log('Creating visit with nurse profile id:', nurseProfile.id);
      console.log('School id:', user?.schoolId);
      console.log('Community member id:', staff.id);
      
      queryClient.setQueryData(
        ['visitStats', user?.schoolId, activeYear.id, activeTerm.id],
        (old: any) => ({
          ...old,
          todayVisits: (old?.todayVisits || 0) + 1,
          weekVisits: (old?.weekVisits || 0) + 1,
        })
      );

      const { error } = await supabase
        .from('visits')
        .insert([{
          community_member_id: staff.id,
          complaint: complaints.join(", "),
          diagnosis: diagnoses.join(", "),
          temperature: parseFloat(temperature),
          treatment: treatments.join(", "),
          nurse_id: nurseProfile.id,
          academic_year_id: activeYear.id,
          term_id: activeTerm.id,
          school_id: user?.schoolId,
          date: new Date().toISOString(),
          requires_followup: requiresFollowup
        }]);

      if (error) throw error;

      setComplaints([]);
      setDiagnoses([]);
      setTreatments([]);
      setTemperature("36.2");
      setRequiresFollowup(false);

      onOpenChange(false);
      onVisitSaved?.();

      toast({
        title: "Success",
        description: "Visit recorded successfully",
      });

    } catch (error) {
      console.error("Error recording visit:", error);
      
      queryClient.invalidateQueries({ 
        queryKey: ['visitStats', user?.schoolId]
      });
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to record visit",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!staff) return null;

  // Display loading state while profile is being fetched
  if (isLoadingProfile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <div className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-center text-sm">Loading nurse profile...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Staff Visit - {staff.name}</DialogTitle>
        </DialogHeader>
        
        {!nurseProfile?.id ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm font-medium">
              Nurse profile not found. Please make sure you're logged in properly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Temperature</Label>
              <Input
                type="number"
                step="0.1"
                min="30"
                max="45"
                value={temperature}
                onChange={handleTemperatureChange}
                placeholder="Enter temperature in °C"
                className={temperatureError ? "border-red-500" : ""}
              />
              {temperatureError && (
                <p className="text-sm text-red-500 mt-1">{temperatureError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Complaints</Label>
              <AutosuggestInput
                type="COMPLAINT"
                value={complaint}
                onChange={setComplaint}
                placeholder="Enter complaints"
                onAddItem={(item) => setComplaints([...complaints, item])}
                selectedItems={complaints}
                onRemoveItem={(item) => setComplaints(complaints.filter(i => i !== item))}
              />
            </div>

            <div className="space-y-2">
              <Label>Diagnosis</Label>
              <AutosuggestInput
                type="DIAGNOSIS"
                value={diagnosis}
                onChange={setDiagnosis}
                placeholder="Enter diagnosis"
                onAddItem={(item) => setDiagnoses([...diagnoses, item])}
                selectedItems={diagnoses}
                onRemoveItem={(item) => setDiagnoses(diagnoses.filter(i => i !== item))}
              />
            </div>

            <div className="space-y-2">
              <Label>Treatment</Label>
              <AutosuggestInput
                type="TREATMENT"
                value={treatment}
                onChange={setTreatment}
                placeholder="Enter treatment"
                onAddItem={(item) => setTreatments([...treatments, item])}
                selectedItems={treatments}
                onRemoveItem={(item) => setTreatments(treatments.filter(i => i !== item))}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="followup"
                checked={requiresFollowup}
                onCheckedChange={(checked) => setRequiresFollowup(!!checked)}
              />
              <label
                htmlFor="followup"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Requires Follow-up
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting || !nurseProfile?.id}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Treatment'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
